import { identityKey, type LevelIdentity } from '../model/identity';
import type { GeneratedLevel, SerializedGeneratedLevel } from '../model/level';
import { deserializeLevel, serializeLevel } from '../model/serialization';
import {
  clearStore,
  deleteRecord,
  getRecord,
  listKeys,
  openDatabase,
  putRecord,
  type StoreName,
} from './db';
import {
  createEmptyProgress,
  DEFAULT_SETTINGS,
  sanitizeInterruptedState,
  sanitizeProgress,
  sanitizeSettings,
  SAVE_SCHEMA_VERSION,
  STORE,
  type CampaignProgress,
  type InterruptedLevelState,
  type LevelCompletion,
  type Settings,
} from './schema';

/**
 * Repository facade over IndexedDB.
 *
 * If storage is unavailable or fails at any point (private browsing, quota exhaustion,
 * corrupted database) the repository transparently degrades to an in-memory store so the
 * game stays playable; `storageAvailable` reports the truth to the UI.
 */

const SETTINGS_KEY = 'settings';
const META_KEY = 'meta';
/** Small, synchronously readable mirror so first paint does not flash the wrong theme. */
const SETTINGS_MIRROR_KEY = 'arrows.settings.v1';
const MAX_CACHED_LEVELS = 120;

export interface RepositoryStatus {
  readonly storageAvailable: boolean;
  readonly degradedReason?: string;
}

export class Repository {
  private database: IDBDatabase | null = null;
  private memory = new Map<string, unknown>();
  private available = false;
  private reason: string | undefined;

  async open(): Promise<RepositoryStatus> {
    try {
      this.database = await openDatabase();
      this.available = true;
      await this.write(STORE.meta, META_KEY, { schemaVersion: SAVE_SCHEMA_VERSION });
    } catch (error) {
      this.database = null;
      this.available = false;
      this.reason = error instanceof Error ? error.message : String(error);
    }
    return this.status();
  }

  status(): RepositoryStatus {
    return this.available
      ? { storageAvailable: true }
      : {
          storageAvailable: false,
          ...(this.reason === undefined ? {} : { degradedReason: this.reason }),
        };
  }

  close(): void {
    this.database?.close();
    this.database = null;
  }

  private memoryKey(store: StoreName, key: string): string {
    return `${store}:${key}`;
  }

  private async read<T>(store: StoreName, key: string): Promise<T | undefined> {
    if (this.database !== null) {
      try {
        return await getRecord<T>(this.database, store, key);
      } catch (error) {
        this.degrade(error);
      }
    }
    return this.memory.get(this.memoryKey(store, key)) as T | undefined;
  }

  private async write(store: StoreName, key: string, value: unknown): Promise<void> {
    this.memory.set(this.memoryKey(store, key), value);
    if (this.database !== null) {
      try {
        await putRecord(this.database, store, key, value);
      } catch (error) {
        this.degrade(error);
      }
    }
  }

  private async remove(store: StoreName, key: string): Promise<void> {
    this.memory.delete(this.memoryKey(store, key));
    if (this.database !== null) {
      try {
        await deleteRecord(this.database, store, key);
      } catch (error) {
        this.degrade(error);
      }
    }
  }

  /**
   * A failed write must never take gameplay down with it: keep whatever is already in
   * memory, mark storage as degraded and let the UI decide whether to mention it.
   */
  private degrade(error: unknown): void {
    this.available = false;
    this.reason = error instanceof Error ? error.message : String(error);
  }

  // ---------------------------------------------------------------- settings

  async loadSettings(): Promise<Settings> {
    const stored = await this.read<unknown>(STORE.settings, SETTINGS_KEY);
    if (stored !== undefined) return sanitizeSettings(stored);
    return sanitizeSettings(readSettingsMirror());
  }

  async saveSettings(settings: Settings): Promise<void> {
    writeSettingsMirror(settings);
    await this.write(STORE.settings, SETTINGS_KEY, settings);
  }

  // ---------------------------------------------------------------- progress

  async loadProgress(campaignId: string): Promise<CampaignProgress> {
    const stored = await this.read<unknown>(STORE.progress, campaignId);
    if (stored === undefined) return createEmptyProgress(campaignId);
    return sanitizeProgress(stored, campaignId);
  }

  async saveProgress(progress: CampaignProgress): Promise<void> {
    await this.write(STORE.progress, progress.campaignId, progress);
  }

  /** Merges a completion, keeping the player's best result for each statistic. */
  async recordCompletion(
    campaignId: string,
    completion: LevelCompletion,
    unlockNext: boolean,
  ): Promise<CampaignProgress> {
    const progress = await this.loadProgress(campaignId);
    const previous = progress.completions[completion.levelNumber];
    const merged: LevelCompletion =
      previous === undefined
        ? completion
        : {
            ...completion,
            bestTimeMs: Math.min(previous.bestTimeMs, completion.bestTimeMs),
            minMistakes: Math.min(previous.minMistakes, completion.minMistakes),
            minHintsUsed: Math.min(previous.minHintsUsed, completion.minHintsUsed),
            attempts: previous.attempts + 1,
          };

    const next: CampaignProgress = {
      ...progress,
      completions: { ...progress.completions, [completion.levelNumber]: merged },
      highestUnlockedLevel: unlockNext
        ? Math.max(progress.highestUnlockedLevel, completion.levelNumber + 1)
        : progress.highestUnlockedLevel,
      tutorialCompleted: progress.tutorialCompleted || completion.levelNumber >= 5,
    };
    await this.saveProgress(next);
    return next;
  }

  // ----------------------------------------------------------- level caching

  async loadCachedLevel(identity: LevelIdentity): Promise<GeneratedLevel | null> {
    const key = identityKey(identity);
    const stored = await this.read<SerializedGeneratedLevel>(STORE.levels, key);
    if (stored === undefined) return null;
    try {
      // deserializeLevel recomputes and compares the board hash, so a record written by a
      // different generator version can never be silently played.
      const level = deserializeLevel(stored);
      if (identityKey(level.identity) !== key) return null;
      return level;
    } catch {
      await this.remove(STORE.levels, key);
      return null;
    }
  }

  async cacheLevel(level: GeneratedLevel): Promise<void> {
    await this.write(STORE.levels, identityKey(level.identity), serializeLevel(level));
    await this.pruneLevelCache();
  }

  private async pruneLevelCache(): Promise<void> {
    if (this.database === null) return;
    try {
      const keys = await listKeys(this.database, STORE.levels);
      if (keys.length <= MAX_CACHED_LEVELS) return;
      // Keys sort by campaign then level number; dropping the lowest levels first keeps the
      // cache useful for where the player actually is.
      const excess = keys.sort().slice(0, keys.length - MAX_CACHED_LEVELS);
      for (const key of excess) await this.remove(STORE.levels, key);
    } catch (error) {
      this.degrade(error);
    }
  }

  // -------------------------------------------------------- interrupted play

  async loadInterrupted(campaignId: string): Promise<InterruptedLevelState | null> {
    const stored = await this.read<unknown>(STORE.sessions, campaignId);
    if (stored === undefined) return null;
    return sanitizeInterruptedState(stored);
  }

  async saveInterrupted(campaignId: string, state: InterruptedLevelState): Promise<void> {
    await this.write(STORE.sessions, campaignId, state);
  }

  async clearInterrupted(campaignId: string): Promise<void> {
    await this.remove(STORE.sessions, campaignId);
  }

  // ------------------------------------------------------------------ resets

  async resetProgress(campaignId: string): Promise<void> {
    await this.remove(STORE.progress, campaignId);
    await this.clearInterrupted(campaignId);
    if (this.database !== null) {
      try {
        await clearStore(this.database, STORE.levels);
      } catch (error) {
        this.degrade(error);
      }
    }
    for (const key of [...this.memory.keys()]) {
      if (key.startsWith(`${STORE.levels}:`)) this.memory.delete(key);
    }
  }
}

/**
 * Resolves a usable Storage, or null. Some runtimes expose a `localStorage` global that is
 * present but not functional, and access itself can throw under strict privacy settings.
 */
function getLocalStorage(): Storage | null {
  try {
    const storage = globalThis.localStorage as Storage | undefined;
    if (storage === undefined || typeof storage.getItem !== 'function') return null;
    return storage;
  } catch {
    return null;
  }
}

function readSettingsMirror(): unknown {
  try {
    const raw = getLocalStorage()?.getItem(SETTINGS_MIRROR_KEY);
    return raw === null || raw === undefined ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function writeSettingsMirror(settings: Settings): void {
  try {
    getLocalStorage()?.setItem(SETTINGS_MIRROR_KEY, JSON.stringify(settings));
  } catch {
    // Private browsing or a full quota: the IndexedDB copy remains the source of truth.
  }
}

/** Synchronous best-effort read used before the database opens, to avoid a theme flash. */
export function readSettingsSync(): Settings {
  const mirror = readSettingsMirror();
  return mirror === undefined ? DEFAULT_SETTINGS : sanitizeSettings(mirror);
}
